import { Component, Panel, Layout, Splitter, Label, ClickEvent, Cursor, KeyHandler } from "@mcleod/components";
import { ComponentProps } from "@mcleod/components/src/base/ComponentProps";
import { DomMouseEvent } from "@mcleod/components/src/events/DomEvent";
import { MouseEvent } from "@mcleod/components/src/events/MouseEvent";
import { RowLayoutListFields } from "@mcleod/components/src/models/ModelLayoutList";
import { ArrayUtil, AuthType, HorizontalAlignment, Keys } from "@mcleod/core";
import { ModelUserHomePage } from "../models/ModelUserHomePage";
import { HomePageEditorGrid } from "./HomePageEditorGrid";
import { numCols, numRows, Point } from "./HomePageUtil";
import { HomeWidget } from "./HomeWidget";
import { PanelDraggableLayoutTree } from "./PanelDraggableLayoutTree";

export class HomePageEditor extends Layout {
  splitTools: Splitter;
  panelTools: Panel;
  gridPanel: HomePageEditorGrid;
  panelLayoutList: PanelDraggableLayoutTree;
  comps: Component[];
  draggedWidget: HomeWidget;
  private _selectedWidget: HomeWidget;
  panelComps: Panel;
  dragIndicator: Panel;
  widgets: HomeWidget[] = [];
  mouseUpListener: (event: DomMouseEvent) => void;

  constructor(props?) {
    super({
      auth: AuthType.ANY,
      title: "Edit Home Page",
      fillRow: true,
      needsServerLayout: false,
      ...props
    });
    this.dragIndicator = new Panel({ style: { position: "absolute", opacity: ".1", pointerEvents: "none" }, backgroundColor: "primary.light" });
    this.panelTools = new Panel({ fillHeight: true, padding: 0 });
    this.panelLayoutList = new PanelDraggableLayoutTree(this, { fillRow: true, fillHeight: true });
    this.panelTools.add(new Label({ caption: "Available Widgets", themeKey: "cta", align: HorizontalAlignment.CENTER, fillRow: true }));
    this.panelTools.add(this.panelLayoutList);
    this.gridPanel = new HomePageEditorGrid({ fillRow: true, fillHeight: true });
    this.splitTools = new Splitter({ firstComponent: this.panelTools, secondComponent: this.gridPanel, fillHeight: true, fillRow: true, position: 280, expandButtonsVisible: false });
    this.gridPanel.addMouseMoveListener(event => this.dragOver(event));
    this.gridPanel.addClickListener(event => this.gridClicked(event));

    this.mouseUpListener = event => this.mouseUp(event);
    this.add(this.splitTools);
    this.loadDefinition();
  }

  override getKeyHandlers(): KeyHandler[] {
    return [{ key: Keys.DELETE, listener: () => this.deleteSelected() }];
  }

  loadDefinition() {
    new ModelUserHomePage().searchSingle().then(userHomePage => {
      if (userHomePage != null) {
        const def = JSON.parse(userHomePage.get("definition"));
        // this.layout.displayLayout(def);
      }
    });
  }

  deleteSelected() {
    if (this.selectedWidget == null)
      return;
    ArrayUtil.removeFromArray(this.widgets, this.selectedWidget);
    this.gridPanel.remove(this.selectedWidget);
  }

  gridClicked(event: ClickEvent) {
    this.selectedWidget = null;
  }

  dragStart(path: string, row: RowLayoutListFields) {
    this.dragWidget(new HomeWidget({ layoutName: path, cellHeight: 2, cellWidth: 2, cellX: 0, cellY: 0, style: { resize: "both"} }));
  }

  dragWidget(widget: HomeWidget) {
    this.draggedWidget = widget;
    window.addEventListener("mouseup", this.mouseUpListener);
  }

  mouseUp(event: DomMouseEvent) {
    if (this.gridPanel.containsPoint(event.offsetX, event.offsetY))
      this.dropped(event);
    this.removeDragIndicator();
    window.removeEventListener("mouseup", this.mouseUpListener);
  }

  removeDragIndicator() {
    if (this.gridPanel._element.contains(this.dragIndicator._element))
      this.gridPanel._element.removeChild(this.dragIndicator._element);
    this.draggedWidget = null;
  }

  getCellAtPoint(x: number, y: number): Point {
    const rect = this.gridPanel.bounds;
    const cellWidth = rect.width / numCols;
    const cellHeight = rect.height / numRows;
    return {
      x: Math.floor((x - (x % cellWidth)) / cellWidth),
      y: Math.floor((y - (y % cellHeight)) / cellHeight)
    };
  }

  dropped(event: DomMouseEvent) {
    const cell = this.getCellAtPoint(event.offsetX, event.offsetY);
    const overlap = this.getWidgetAtPoint(event.offsetX, event.offsetY, this.draggedWidget.cellWidth, this.draggedWidget.cellHeight);
    if (overlap == null || overlap === this.draggedWidget) {
      const widget = this.draggedWidget;
      widget.cellX = cell.x;
      widget.cellY = cell.y;
      if (!this.widgets.includes(widget)) {
        this.widgets.push(widget);
        this.gridPanel.add(widget);
        widget.addClickListener(_event => this.selectedWidget = widget);
        widget.addMouseDownListener(_event => this.dragWidget(widget));
      }
      widget.setProps(this.getDraggedProps(cell));
      this.selectedWidget = widget;
    }
    this.removeDragIndicator();
  }

  dragOver(event: MouseEvent) {
    event.shouldAutomaticallyStopPropagation = false;
    if (this.draggedWidget == null)
      return;
    if (!this.gridPanel._element.contains(this.dragIndicator._element))
      this.gridPanel._element.appendChild(this.dragIndicator._element);
    const cell = this.getCellAtPoint(event.domEvent.offsetX, event.domEvent.offsetY);
    this.dragIndicator.setProps(this.getDraggedProps(cell));
    const overlap = this.getWidgetAtPoint(event.domEvent.offsetX, event.domEvent.offsetY, this.draggedWidget.cellWidth, this.draggedWidget.cellHeight);
    if (overlap == null || overlap === this.draggedWidget)
      this.dragIndicator.backgroundColor = "primary.light";
    else
      this.dragIndicator.backgroundColor = "error.light";
  }

  getDraggedProps(cell: Point): Partial<ComponentProps> {
    return {
      left: ((cell.x / numCols) * 100) + "%",
      top: ((cell.y / numRows) * 100) + "%",
      width: ((100 / numCols) * this.draggedWidget.cellWidth) + "%",
      height: ((100 / numRows) * this.draggedWidget.cellHeight) + "%"
    };
  }

  getWidgetAtPoint(x: number, y: number, cellWidth: number = 0, cellHeight: number = 0): HomeWidget {
    for (const widget of this.widgets) {
      if (widget.containsPoint(x, y))
        return widget;
    }
    return null;
  }


  get selectedWidget(): HomeWidget {
    return this._selectedWidget;
  }

  set selectedWidget(value: HomeWidget) {
    if (this._selectedWidget != null)
      this._selectedWidget.borderWidth = 0;
    this._selectedWidget = value;
    if (value != null) {
      value.borderWidth = 1;
      value.cursor = Cursor.EW_RESIZE;
    }
  }

  save(isNew: boolean) {
    // const ser = serializeComponents(this.layout, null);
    // const deser = JSON.parse(ser);
    // const trimmed = { columns: deser.columns, expandComponent: deser.expandComponent };
    // const definition = JSON.stringify(trimmed);
    // const row = new RowUserHomePage();
    // row.set({ definition: definition });
    // row._appending = isNew;
    // row.post()
    //   .then(response => Snackbar.showSnackbar("Saved home page"))
    //   .catch(error => showError(error));
  }

  getRouterProps() {
    return { padding: 0 };
  }
}
